import { useMemo, useState } from 'react';
import { Empty, ErrorBanner, Loading, Modal } from '../../components/ui';
import { api } from '../../lib/api';
import { downloadCsv, fileStamp } from '../../lib/download';
import { formatNumber } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import type {
  AmClientCategory,
  AmProductMatrix as Matrix,
  ProductCategoryRow,
} from '../../lib/types';

/**
 * Which categories of the price book a client buys.
 *
 * The rows are the catalogue's own categories — see migration 057 — so this
 * page and the Product Catalogue are the same list by construction. It used to
 * tick individual products from a hard-coded list that was nobody's real
 * catalogue.
 *
 * NOTHING SAVES UNTIL EDIT IS PRESSED. The old version wrote on every click,
 * which is fine for a form somebody opened to fill in and wrong for a page
 * people mostly read: a mis-click on a review call silently changed what the
 * client is recorded as buying, and nothing said so. Now the default state is
 * a read-only list, Edit turns the boxes on, and Save writes once.
 *
 * A tick placed by an accepted recommendation says so. When it is wrong, the
 * first question is who claimed it — and "the Deploy EDR recommendation, when
 * it was approved" is a better answer than a checkbox with no history.
 */
export function AmProductMatrixCard({
  clientId,
  initial,
}: {
  clientId: string;
  initial: AmClientCategory[];
}) {
  const categories = useResource<ProductCategoryRow[]>('/am/product-categories');

  const [owned, setOwned] = useState<AmClientCategory[]>(initial);
  const [editing, setEditing] = useState(false);
  /* The edit's working copy. Kept separate so Cancel is a real cancel rather
     than a re-fetch, and so nothing is written until Save. */
  const [draft, setDraft] = useState<number[]>([]);
  const [search, setSearch] = useState('');
  const [ownedOnly, setOwnedOnly] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const rows = categories.data ?? [];
  const ownedIds = useMemo(() => new Set(owned.map((o) => o.CategoryId)), [owned]);
  const sourceOf = useMemo(
    () => new Map(owned.map((o) => [o.CategoryId, o])),
    [owned],
  );

  const startEdit = () => {
    setDraft([...ownedIds]);
    setEditing(true);
    setError(null);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put(`/am/clients/${clientId}/product-categories`, { categoryIds: draft });
      /* Provenance is decided by the server — a tick that survives keeps the
         recommendation it came from — so the local copy is rebuilt from what
         was there plus whatever is new. */
      setOwned(
        draft.map(
          (id) =>
            sourceOf.get(id) ?? {
              CategoryId: id,
              Name: rows.find((r) => r.CategoryId === id)?.Name ?? '',
              Source: 'manual' as const,
              FromRecommendationId: null,
            },
        ),
      );
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  if (categories.loading) return <Loading label="Loading the categories" />;
  if (categories.error) return <ErrorBanner message={categories.error} />;

  const q = search.trim().toLowerCase();
  const visible = rows.filter((r) => {
    const on = editing ? draft.includes(r.CategoryId) : ownedIds.has(r.CategoryId);
    if (ownedOnly && !on) return false;
    return !q || r.Name.toLowerCase().includes(q);
  });

  const toggle = (categoryId: number) =>
    setDraft((d) => (d.includes(categoryId) ? d.filter((x) => x !== categoryId) : [...d, categoryId]));

  const count = editing ? draft.length : ownedIds.size;
  const fromRecommendations = owned.filter((o) => o.Source === 'recommendation').length;

  return (
    <section className="card">
      <div className="card-head">
        <div className="grow">
          <span className="eyebrow">What they buy</span>
          <h2>Product matrix</h2>
          <p className="sub">
            {count} of {rows.length} categories on this account
            {fromRecommendations > 0 && !editing && (
              <> · {fromRecommendations} from an accepted recommendation</>
            )}
          </p>
        </div>
        <div className="row wrap">
          {editing ? (
            <>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={saving}
                onClick={() => setEditing(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={saving}
                onClick={() => void save()}
              >
                {saving ? 'Saving…' : 'Save matrix'}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setExporting(true)}
              >
                Export matrix
              </button>
              {/* The whole point of the edit mode: a tick cannot change while
                  somebody is reading the page. */}
              <button type="button" className="btn btn-ghost btn-sm" onClick={startEdit}>
                Edit
              </button>
            </>
          )}
        </div>
      </div>

      <div className="card-body">
        {error && <ErrorBanner message={error} />}

        {editing && (
          <p className="small muted mb-4">
            Ticking here records what the client buys. Nothing is saved until you press Save.
          </p>
        )}

        <div className="row wrap mb-4">
          <input
            className="input"
            style={{ maxWidth: 280 }}
            placeholder="Search categories…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search categories"
          />
          <label className="checkbox">
            <input
              type="checkbox"
              checked={ownedOnly}
              onChange={(e) => setOwnedOnly(e.target.checked)}
            />
            Purchased only
          </label>
        </div>

        {visible.length === 0 ? (
          <Empty title="Nothing matches">
            <p>
              {rows.length === 0
                ? 'The price book has no categories yet. They come from the Product Catalogue.'
                : 'No category matches that search.'}
            </p>
          </Empty>
        ) : (
          <div className="am-product-grid">
            {visible.map((r) => {
              const on = editing ? draft.includes(r.CategoryId) : ownedIds.has(r.CategoryId);
              const record = sourceOf.get(r.CategoryId);
              const fromRec = !editing && record?.Source === 'recommendation';
              const className = `am-product${on ? ' on' : ''}${editing ? '' : ' locked'}`;
              const body = (
                <span>
                  {r.Name}
                  <small>
                    {r.ProductCount} {r.ProductCount === 1 ? 'service' : 'services'}
                    {fromRec && ' · from a recommendation'}
                  </small>
                </span>
              );

              /* Read mode renders no input at all rather than a disabled one:
                 a disabled checkbox still reads as something you could switch
                 on if you tried harder, and this is a list, not a form. */
              return editing ? (
                <label key={r.CategoryId} className={className}>
                  <input type="checkbox" checked={on} onChange={() => toggle(r.CategoryId)} />
                  {body}
                </label>
              ) : (
                <div key={r.CategoryId} className={className}>
                  <span className="am-tick" aria-hidden="true">
                    {on ? '✓' : ''}
                  </span>
                  {body}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {exporting && <AmMatrixExportModal onClose={() => setExporting(false)} preselect={clientId} />}
    </section>
  );
}

/**
 * The cross-client matrix export.
 *
 * One row per category, one column per client, so it reads as a gap analysis:
 * you can see at a glance who has backup and who does not. Client-per-row would
 * put forty-four columns on the page and answer nothing.
 */
export function AmMatrixExportModal({
  onClose,
  preselect,
}: {
  onClose: () => void;
  preselect?: string;
}) {
  const matrix = useResource<Matrix>('/am/product-matrix');
  const [picked, setPicked] = useState<string[] | null>(null);
  const [ownedOnly, setOwnedOnly] = useState(true);

  const clients = matrix.data?.clients ?? [];
  /* Default: everyone, or just the client the export was opened from. */
  const selected = useMemo(
    () => picked ?? (preselect ? [preselect] : clients.map((c) => c.AmClientId)),
    [picked, preselect, clients],
  );

  const toggle = (id: string) =>
    setPicked(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  function exportCsv() {
    const data = matrix.data;
    if (!data) return;
    const chosen = data.clients.filter((c) => selected.includes(c.AmClientId));
    const rows: unknown[][] = [];

    rows.push(['Category', ...chosen.map((c) => c.Name), 'Clients']);
    rows.push(['Monthly recurring revenue', ...chosen.map((c) => c.MonthlyRevenue ?? 0), '']);
    rows.push(['Account manager', ...chosen.map((c) => c.AccountManager ?? 'Unassigned'), '']);

    for (const category of data.categories) {
      const marks = chosen.map((c) => (c.categoryIds.includes(category.CategoryId) ? 'Yes' : ''));
      const count = marks.filter(Boolean).length;
      /* "Purchased only" drops categories nobody in the selection buys — the
         difference between a gap analysis and a 44-row catalogue printout. */
      if (ownedOnly && count === 0) continue;
      rows.push([category.Name, ...marks, count]);
    }

    rows.push(['Categories purchased', ...chosen.map((c) => c.categoryIds.length), '']);

    downloadCsv(`product-matrix-${fileStamp()}.csv`, rows);
    onClose();
  }

  return (
    <Modal
      title="Export the product matrix"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={selected.length === 0}
            onClick={exportCsv}
          >
            Download CSV
          </button>
        </>
      }
    >
      {matrix.loading ? (
        <Loading label="Loading the matrix" />
      ) : (
        <>
          <p className="small muted">
            One row per category, one column per client. Opens in Excel, Numbers or Sheets.
          </p>
          <label className="checkbox mt-2">
            <input
              type="checkbox"
              checked={ownedOnly}
              onChange={(e) => setOwnedOnly(e.target.checked)}
            />
            Only categories at least one selected client buys
          </label>

          <div className="row-between mt-4">
            <span className="eyebrow">Clients ({selected.length})</span>
            <span className="row">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setPicked(clients.map((c) => c.AmClientId))}
              >
                All
              </button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPicked([])}>
                None
              </button>
            </span>
          </div>

          <div className="stack mt-2" style={{ gap: 'var(--space-1)' }}>
            {clients.map((c) => (
              <label key={c.AmClientId} className="checkbox">
                <input
                  type="checkbox"
                  checked={selected.includes(c.AmClientId)}
                  onChange={() => toggle(c.AmClientId)}
                />
                {c.Name}
                <span className="xs muted">
                  {c.categoryIds.length} categories · {formatNumber(c.MonthlyRevenue, 'currency')}/mo
                </span>
              </label>
            ))}
          </div>
        </>
      )}
    </Modal>
  );
}
